import { saveImage } from '@/utils/compat';
import { logger } from '@/utils/logger';

/**
 * Holds a generated photo that could not be saved automatically because iOS
 * requires a live user gesture for the share sheet (the tap that started the
 * flow expired during watermark generation, or the user dismissed the sheet).
 *
 * PendingSaveOverlay (mounted once in App.tsx) subscribes here and shows a
 * "Save photo" button — a fresh tap iOS will honor — so the photo is never
 * silently lost. Deliberately not in the Zustand store: this is transient
 * UI plumbing, not app state to persist or sync.
 */
export interface PendingSavePhoto {
  src: string;
  filename: string;
}

type Listener = (pending: PendingSavePhoto | null) => void;

let current: PendingSavePhoto | null = null;
const listeners = new Set<Listener>();

export function subscribePendingSave(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => {
    listeners.delete(listener);
  };
}

export function setPendingSave(pending: PendingSavePhoto | null): void {
  current = pending;
  listeners.forEach((listener) => listener(current));
}

/**
 * Save an image, and if the platform needs a fresh user gesture (iOS share
 * sheet), queue it for the global PendingSaveOverlay instead of dropping it.
 * Callers can fire-and-continue — the overlay takes over from here.
 */
export async function saveImageOrQueue(src: string, filename: string): Promise<void> {
  const result = await saveImage(src, filename);
  if (result === 'needs-gesture') {
    logger.info('Save needs a user gesture — queued for PendingSaveOverlay', { filename });
    setPendingSave({ src, filename });
  }
}
